import { axiosInstance } from "../../Authentication/AxiosInterceptor";
import type {
  KeyResult,
  KpiLink,
  KpiProgressNote,
  KpiProgressNoteComment,
  KpiProgressStatus,
} from "../../Types/KPI/IAreaKpi";

export type KpiAreaApiType = "Evaluate" | "Develop" | "Improve";

/** Discriminator for the {@link patchKpiLink} request; must match server enum. */
export type KpiLinkPatchOperationType =
  | "AddProgressNote"
  | "DeleteProgressNote"
  | "UpdateProgressNoteComment"
  | "UpdateProgressNoteStatus";

/**
 * Single-property patch payload sent to the `PatchKPILink` endpoint.
 * Only the fields relevant to {@link operation} need to be populated.
 */
export type PatchKpiLinkRequest = {
  operation: KpiLinkPatchOperationType;
  /** Required for note-scoped ops (Delete / UpdateProgressNoteComment / UpdateProgressNoteStatus). */
  progressNoteId?: string;
  /** Required for `AddProgressNote`. */
  progressNote?: KpiProgressNote;
  /** Required for `UpdateProgressNoteComment`. */
  comment?: KpiProgressNoteComment;
  /** Required for `UpdateProgressNoteStatus`. */
  status?: KpiProgressStatus;
};

function mergeKpiNoteComment(
  comment: KpiProgressNoteComment | undefined,
): KpiProgressNoteComment | undefined {
  if (!comment) return undefined;
  const text = comment.commentText ?? comment.CommentText ?? "";
  return { ...comment, commentText: text };
}

/** Cosmos / older payloads may use `StatusLabel` instead of `statusLabel`. */
function normalizeKpiProgressStatus(
  status: KpiProgressNote["status"],
): KpiProgressNote["status"] | undefined {
  if (!status) return undefined;
  const statusLabel = (status.statusLabel ?? status.StatusLabel ?? "").trim();
  return {
    ...status,
    ...(statusLabel ? { statusLabel } : {}),
    statusColour: status.statusColour ?? status.StatusColour,
  };
}

/** Map server/Cosmos comment field names onto the in-app `commentText` field. */
export function normalizeKpiLink(link: KpiLink): KpiLink {
  const notes = link.kpiProgressNotes;
  if (!notes?.length) return link;
  return {
    ...link,
    kpiProgressNotes: notes.map((note) => ({
      ...note,
      comment: mergeKpiNoteComment(note.comment),
      status: normalizeKpiProgressStatus(note.status),
    })),
  };
}

export async function getKpiLinksByArea(
  areaType: KpiAreaApiType,
  areaId: string,
): Promise<KpiLink[]> {
  const response = await axiosInstance.get<KpiLink[]>(
    `GetKPILinksByArea/${areaType}/${areaId}`,
  );
  return (response.data ?? []).map(normalizeKpiLink);
}

export async function getKpiDataByArea(
  areaType: KpiAreaApiType,
  areaId: string,
): Promise<KeyResult[]> {
  const response = await axiosInstance.get<KeyResult[]>(
    `GetKPIDataByArea/${areaType}/${areaId}`,
  );
  return response.data ?? [];
}

/** Cosmos lookup by id; `id` query must match partition key (key result id). */
export async function getKpiById(
  clientId: string,
  keyResultId: string,
): Promise<KeyResult | null> {
  try {
    const response = await axiosInstance.get<KeyResult>(`GetKPIById/${clientId}`, {
      params: { id: keyResultId },
    });
    return response.data ?? null;
  } catch {
    return null;
  }
}

export async function updateKpiLink(
  clientId: string,
  link: KpiLink,
): Promise<KpiLink> {
  const response = await axiosInstance.put<KpiLink>(`UpdateKPILink/${clientId}`, link);
  return normalizeKpiLink(response.data ?? link);
}

/**
 * Patches one property of a single KPI link without round-tripping the full document.
 * Server resolves the affected element (e.g. a progress note) by id and applies a single
 * Cosmos patch operation. Returns the updated link.
 */
export async function patchKpiLink(
  clientId: string,
  linkId: string,
  request: PatchKpiLinkRequest,
): Promise<KpiLink> {
  const response = await axiosInstance.patch<KpiLink>(
    `PatchKPILink/${clientId}/${linkId}`,
    request,
  );
  return normalizeKpiLink(response.data ?? ({} as KpiLink));
}
